import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import FolderSelect from './FolderSelect'
import { useTranslation } from 'react-i18next'

interface BulkMoveModalProps {
  isOpen: boolean
  selectedCount: number
  isBusy?: boolean
  onConfirm: (folderId: string | null) => void
  onCancel: () => void
}

/** 선택한 링크들을 옮길 폴더를 고르는 모달 */
export default function BulkMoveModal({
  isOpen,
  selectedCount,
  isBusy = false,
  onConfirm,
  onCancel,
}: BulkMoveModalProps) {
  const { t } = useTranslation()
  const [folderId, setFolderId] = useState<string | null>(null)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCancel}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-move-title"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            onClick={event => event.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800"
          >
            <h2
              id="bulk-move-title"
              className="text-base font-semibold text-gray-900 dark:text-white"
            >
              {t('bookmark.bulk.moveTitle', { count: String(selectedCount) })}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {t('bookmark.bulk.moveDescription')}
            </p>

            <div className="mt-4">
              <FolderSelect
                value={folderId}
                onChange={setFolderId}
                placeholder={t('bookmark.bulk.noFolder')}
                disabled={isBusy}
              />
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onConfirm(folderId)}
                className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-700 disabled:opacity-60"
              >
                {isBusy
                  ? t('bookmark.bulk.moving')
                  : t('bookmark.bulk.moveSubmit')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
